import { wrapper, createApiClient, Result, ApiError } from "../base";
import DimensionDO = App.Data.DimensionDO;
import DimensionValueDO = App.Data.DimensionValueDO;
import NominalValuesPutDTO = App.Data.NominalValuesPutDTO;
import DimensionBucketDO = App.Data.DimensionBucketDO;
import BucketPutDTO = App.Data.BucketPutDTO;
import BucketCreationDTO = App.Data.BucketCreationDTO;

const basePath = `dimensions`;
const baseClient = createApiClient();

type DimensionParams = {
	"filter[type]"?: string;
};

type DimensionValuesParams = {
	"filter[label]"?: string;
	"include[dimension]"?: boolean;
};

export default class {
	get(params: DimensionParams = {}): Promise<Result<DimensionDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}`, { params }));
	}

	// ================================ Buckets ================================

	getBuckets(
		dimensionId: number
	): Promise<
		Result<{ dimension: DimensionDO; buckets: DimensionBucketDO[] }, ApiError>
	> {
		return wrapper(baseClient.get(`${basePath}/${dimensionId}/buckets`));
	}

	addBucket(
		dimensionId: number,
		bucket: BucketCreationDTO
	): Promise<Result<void, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/${dimensionId}/buckets`, bucket)
		);
	}

	deleteBucket(bucketId: number): Promise<Result<void, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/bucketed/${bucketId}`));
	}

	generateFromClickhouse(
		dimensionId: number,
		name: string,
		valueCount: number
	): Promise<Result<number[], ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/${dimensionId}/clickhouse-distribution`, {
				name: name,
				value_count: valueCount,
			})
		);
	}

	setBucketAsDefault(bucketId: number): Promise<Result<void, ApiError>> {
		return wrapper(baseClient.put(`${basePath}/set-as-default/${bucketId}`));
	}

	// ============================== Bucket Values =============================

	getBucketValues(
		bucketId: number
	): Promise<Result<DimensionValueDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/bucketed/${bucketId}/values`));
	}

	setBucketValues(data: BucketPutDTO): Promise<Result<void, ApiError>> {
		return wrapper(
			baseClient.put(`${basePath}/bucketed/${data.bucket.id}/values`, data)
		);
	}

	// =========================== Nominal Dimensions ===========================

	getNominalValues(
		params: DimensionValuesParams = {},
		includedDimensionIds: number[] | null = null,
		limit: number | null = null,
		includeDimension: boolean = true
	): Promise<Result<DimensionValueDO[], ApiError>> {
		const queryParams: Record<string, any> = {
			...params,
			includedDimensionIds,
			limit,
		};
		if (includeDimension) {
			queryParams.include = "dimension";
		}

		return wrapper(
			baseClient.get(`${basePath}/nominal/values`, {
				params: queryParams,
			})
		);
	}

	setNominalValues(
		dimensionId: number,
		data: NominalValuesPutDTO
	): Promise<Result<void, ApiError>> {
		return wrapper(
			baseClient.put(`${basePath}/nominal/${dimensionId}/values`, data)
		);
	}
}
